/**
 * The type scale, as class strings.
 *
 * Counted before this file existed: the small band alone used eleven sizes —
 * 0.7, 0.72, 0.75, 0.78, 0.8, 0.82, 0.85, 0.88, 0.9, 0.95 and 1.05rem — and
 * section headings ran 2rem, 2.15rem, 2.2rem, 2.25rem, 2.5rem and 2.6rem with
 * three different responsive curves. That is not a scale, it is a hundred
 * separate decisions, and it is why sections that should rhyme never quite did.
 *
 * Four steps cover the whole site. A component that genuinely needs something
 * else (the `POZNAJ FLOU.` display moment) is an exception, not a new size.
 *
 * ## Which face each step is set in
 *
 * The big end is Outfit — the masthead face: page titles, section headings and
 * the figures in {@link STAT}, which are headline-sized numbers and behave like
 * headings rather than like copy. {@link SUBHEADING} and {@link CARD_TITLE},
 * the two steps that live INSIDE a section, stay in Manrope.
 *
 * Running text — leads, body, labels, the small band — is the body face
 * throughout. That is what makes this a pairing rather than a second font
 * scattered at random.
 *
 * Whatever the faces are, each step names ONE of them, which is the property
 * the scale is for. The heading band used to change typeface mid-sentence (see
 * {@link ACCENT_TEXT}); swapping faces without that fix would only have moved
 * the seam.
 */

/**
 * A page's own title — the `<h1>` band on a service page.
 *
 * The one step set in the MASTHEAD face rather than the heading face. It is
 * the page announcing itself, so it belongs with the navigation above it and
 * with the home page's own headline; everything below — {@link HEADING} down
 * to {@link CARD_TITLE} — is the page saying something, and stays in Manrope.
 */
export const TITLE =
  'font-masthead text-[2.2rem] font-bold leading-[1.02] tracking-tight sm:text-[3.2rem] text-balance'

/**
 * {@link TITLE} on a page whose first screen has to hold something else.
 *
 * Same step, with its desktop size clamped against the window's HEIGHT. An
 * offer page puts a three-card ladder under this headline and the whole thing
 * is one screen (`lib/tier-fit.ts`), so on a laptop the title is competing for
 * room with the prices — and these headlines are two-clause, so 3.2rem costs
 * 104px of the fold rather than 52.
 *
 * Full size from ~1000px of window height up, which is where it stops costing
 * anything. The floor is 2.4rem: a page title has to stay bigger than the
 * section headings under it.
 */
export const TITLE_SCREEN =
  'font-masthead text-[2.2rem] font-bold leading-[1.02] tracking-tight sm:text-[clamp(2.4rem,5.2svh,3.2rem)] text-balance'

/** Section heading. One size, one curve, everywhere. */
export const HEADING =
  'font-masthead text-[2rem] font-bold leading-[1.05] tracking-tight sm:text-[2.6rem]'

/**
 * The heading of a block INSIDE a section — a pricing table, a panel, a step.
 *
 * Between HEADING and CARD_TITLE, and it was the step the service pages kept
 * reinventing: `1.25`, `1.3`, `1.35`, `1.4`, `1.5`, `1.6`, `1.7`, `1.8` and
 * `1.9rem` all appeared in this role, four of them paired with a different
 * `sm:` bump.
 */
export const SUBHEADING = 'font-masthead text-[1.35rem] font-bold tracking-tight sm:text-[1.6rem]'

/**
 * The heading inside a card or list row.
 *
 * The LAST thing on the site still set in Manrope. `SUBHEADING` moved up to
 * the masthead face on the owner's call, which leaves this one token holding
 * the second family — and holding it for a reason rather than by oversight: a
 * card's heading sits at 1.05rem inside a box of body copy, which is text
 * doing a text's job, not a mark. If it ever moves too, Manrope stops being
 * loaded at all and `layout.tsx` should lose the declaration with it.
 */
export const CARD_TITLE = 'font-heading text-[1.05rem] font-bold leading-snug'

/**
 * A number presented as a number — a price, a total, a count.
 *
 * `leading-none` on purpose: these sit next to a label and the normal line box
 * would push them off its baseline.
 */
export const STAT = 'font-masthead text-[1.6rem] font-bold leading-none tracking-tight'

/** The sentence under a heading. */
export const LEAD = 'text-[1.05rem] leading-[1.55] sm:text-[1.1rem]'

/** Running copy inside cards and lists. */
export const BODY = 'text-[0.95rem] leading-relaxed'

/** Labels, captions, metadata. */
export const META = 'text-[0.8rem] leading-normal'

/**
 * The smallest step: chips, table headers, footnotes.
 *
 * Below this, text stops being readable on a phone, so it is the floor — the
 * app had eight sizes under it (0.58, 0.6, 0.62, 0.65, 0.66, 0.68, 0.7, 0.72),
 * several of them set in all-caps with letter-spacing, which is the hardest
 * thing there is to read small.
 *
 * TWELVE PIXELS, not eleven and a half. The floor used to be `0.72rem`, which
 * is under the size browsers and Lighthouse both treat as the limit for legible
 * body text — and the app had a hundred and thirty of them. Half a pixel is not
 * a design decision worth defending; being readable is.
 */
export const MICRO = 'text-[0.75rem] leading-normal'

/**
 * The uppercase label above a headline, and nothing else.
 *
 * Separate from MICRO because the tracking belongs to the all-caps treatment,
 * not to the size: `MICRO` on lowercase text with 0.16em between the letters
 * reads as a typo. The page kickers carried four different spacings (0.12,
 * 0.16, 0.22, 0.24em) at three different sizes.
 */
export const KICKER = 'text-[0.75rem] leading-normal font-semibold tracking-[0.18em] uppercase'

/**
 * The accented clause of a headline — the brand gradient as text fill.
 *
 * This exact 78-character class string was pasted into five section files, and
 * had already drifted: one of them clipped `var(--flou-gradient)` instead, a
 * different ramp entirely. One headline treatment, one definition.
 *
 * **Colour is the whole accent. The typeface is not.** Page headlines used to
 * set their second clause in Archivo at 125% width — a real change of voice
 * mid-sentence, and that was the objection: `Kadr, który` in Geist and
 * `zatrzymuje kciuk.` in a wider, heavier face is one sentence in two fonts,
 * and the seam lands mid-headline on seven pages. A headline is one voice; the
 * gradient is already doing the emphasis.
 *
 * So there is one face across every heading on the site now, and the width
 * axis stays where it reads as a deliberate instrument rather than as a font
 * swap: the prices in `tier-card`, which are figures and not prose.
 */
/*
 * ── ONE RAMP, NOT A SECOND COPY OF IT ──────────────────────────────────
 * This used to write its own three stops, and they had drifted from the ones
 * the hero clips through `.flou-grad-text`: a different angle (90° against
 * 100°), a different middle position (50% against 45%) and — after the stops
 * in `@repo/ui` were brought inside sRGB — a visibly different violet, 0.18
 * chroma here against 0.139 there. Two headlines on one page, both „the brand
 * gradient", ending on two different colours.
 *
 * It now clips `--flou-gradient-text`, which is the same variable the hero
 * uses and the only place those three stops are written down. That also hands
 * this treatment the wide-gamut branch the token carries: on a P3 screen both
 * headlines get the vivid violet, on an ordinary one both get the sRGB value.
 */
export const ACCENT_TEXT = 'bg-[image:var(--flou-gradient-text)] bg-clip-text text-transparent'
